import { useEffect, useState } from 'react'

/**
 * Elements whose children the browser never paints. A host moved into one of these would be as
 * invisible as one left outside the fullscreen element, so the body is the better place for it.
 */
const CHILDLESS = new Set(['VIDEO', 'AUDIO', 'IFRAME', 'CANVAS', 'IMG', 'EMBED', 'OBJECT'])

function placeFor(): Element {
  const fullscreen = document.fullscreenElement

  return fullscreen && !CHILDLESS.has(fullscreen.tagName) ? fullscreen : document.body
}

/**
 * Restart autoplay media the move left paused. A same-task move keeps a `<video>` playing, but one
 * detached in an EARLIER task — a fullscreen dashboard unmounting with the host still inside it —
 * comes back paused, and `autoplay` does not restart it (both measured in Chrome 151). Only
 * autoplay media, so anything someone paused on purpose stays paused.
 */
function resumeAutoplay(host: HTMLElement) {
  host.querySelectorAll<HTMLMediaElement>('video[autoplay], audio[autoplay]').forEach(media => {
    if (media.paused) void media.play()?.catch(() => undefined)
  })
}

/**
 * A node to portal an app-wide overlay into that stays visible while something is fullscreen.
 *
 * In fullscreen the browser paints only the fullscreen element's subtree, so an overlay mounted in
 * `AppLayout` — the SOS call panel — disappears the moment a dashboard goes fullscreen, though it
 * is still mounted and still live. This host follows `document.fullscreenElement` in and back out.
 *
 * The node itself never changes, only its parent, and that is the point: React REMOUNTS a portal's
 * children when its container changes, and for the call panel an unmount is a hang-up. So callers
 * must portal into this one node rather than into `document.fullscreenElement` directly.
 *
 * `null` until the host is attached, so nothing is ever laid out inside a detached node.
 */
export function useFullscreenHost(): HTMLElement | null {
  const [host, setHost] = useState<HTMLElement | null>(null)

  useEffect(() => {
    const node = document.createElement('div')

    const place = () => {
      const parent = placeFor()

      if (node.parentNode === parent) return
      parent.appendChild(node)
      resumeAutoplay(node)
    }

    place()
    setHost(node)
    // The browser fires this at the document too when the fullscreen element is REMOVED (measured),
    // which is what brings the host back when a fullscreen dashboard unmounts.
    document.addEventListener('fullscreenchange', place)

    return () => {
      document.removeEventListener('fullscreenchange', place)
      node.remove()
    }
  }, [])

  return host
}
